import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Home, Users, Stethoscope, Handshake, Building2,
  Receipt, FolderOpen, TrendingUp, Settings, LogOut, Globe2, DollarSign, Trophy, ListChecks,
  Plane, Car, Hotel, LayoutDashboard, FileSignature, FileText,
} from "lucide-react";

// Sectioned navigation. Each section has a label + items.
const NAV = {
  admin: [
    { section: "Operations", items: [
      { to: "/admin",          label: "Dashboard", icon: Home, end: true },
      { to: "/admin/leads",    label: "Leads",     icon: Users },
      { to: "/admin/patients", label: "Patients",  icon: Stethoscope },
      { to: "/admin/tasks",    label: "Tasks",     icon: ListChecks },
    ]},
    { section: "International Services", items: [
      { to: "/admin/visa",           label: "Visa Invitation Requests", icon: FileSignature },
      { to: "/admin/visa/tracker",   label: "Visa Status Tracker",      icon: LayoutDashboard },
      { to: "/admin/visa/dashboard", label: "Visa Operations",          icon: TrendingUp },
      { to: "/admin/visa/travel",    label: "Travel Coordination",      icon: Plane },
      { to: "/admin/visa/pickup",    label: "Airport Pickup",           icon: Car },
      { to: "/admin/visa/hotel",     label: "Hotel Accommodation",      icon: Hotel },
      { to: "/admin/visa/templates", label: "Letter Templates",         icon: FileText },
    ]},
    { section: "Network", items: [
      { to: "/admin/network",     label: "Provider Network", icon: Building2 },
      { to: "/admin/partners",    label: "Partners",         icon: Handshake },
      { to: "/admin/leaderboard", label: "Leaderboard",      icon: Trophy },
    ]},
    { section: "Finance", items: [
      { to: "/admin/quotations",  label: "Quotations",  icon: Receipt },
      { to: "/admin/commissions", label: "Commissions", icon: DollarSign },
    ]},
    { section: "Records", items: [
      { to: "/admin/documents", label: "Documents", icon: FolderOpen },
      { to: "/admin/analytics", label: "Analytics", icon: TrendingUp },
    ]},
    { section: "Account", items: [
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ]},
  ],
  coordinator: [
    { section: "Operations", items: [
      { to: "/coordinator",          label: "Dashboard", icon: Home, end: true },
      { to: "/coordinator/leads",    label: "Leads",     icon: Users },
      { to: "/coordinator/patients", label: "Patients",  icon: Stethoscope },
      { to: "/coordinator/tasks",    label: "Tasks",     icon: ListChecks },
    ]},
    { section: "International Services", items: [
      { to: "/coordinator/visa",           label: "Visa Requests",       icon: FileSignature },
      { to: "/coordinator/visa/tracker",   label: "Status Tracker",      icon: LayoutDashboard },
      { to: "/coordinator/visa/dashboard", label: "Visa Operations",     icon: TrendingUp },
      { to: "/coordinator/visa/travel",    label: "Travel Coordination", icon: Plane },
      { to: "/coordinator/visa/pickup",    label: "Airport Pickup",      icon: Car },
      { to: "/coordinator/visa/hotel",     label: "Hotel Accommodation", icon: Hotel },
    ]},
    { section: "Network", items: [
      { to: "/coordinator/network", label: "Provider Network", icon: Building2 },
    ]},
    { section: "Finance", items: [
      { to: "/coordinator/quotations", label: "Quotations", icon: Receipt },
    ]},
    { section: "Records", items: [
      { to: "/coordinator/documents", label: "Documents", icon: FolderOpen },
    ]},
    { section: "Account", items: [
      { to: "/coordinator/settings", label: "Settings", icon: Settings },
    ]},
  ],
  partner: [
    { section: "Operations", items: [
      { to: "/partner",          label: "Dashboard",    icon: Home, end: true },
      { to: "/partner/leads",    label: "My Referrals", icon: Users },
      { to: "/partner/patients", label: "My Patients",  icon: Stethoscope },
    ]},
    { section: "International Services", items: [
      { to: "/partner/visa", label: "Visa Requests", icon: FileSignature },
    ]},
    { section: "Finance", items: [
      { to: "/partner/commissions", label: "Commissions", icon: DollarSign },
    ]},
    { section: "Account", items: [
      { to: "/partner/settings", label: "Settings", icon: Settings },
    ]},
  ],
  patient: [
    { section: "Journey", items: [
      { to: "/portal", label: "My Journey", icon: Home, end: true },
    ]},
    { section: "International Services", items: [
      { to: "/portal/visa", label: "My Visa Requests", icon: FileSignature },
    ]},
    { section: "Account", items: [
      { to: "/portal/settings", label: "Settings", icon: Settings },
    ]},
  ],
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sections = NAV[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside
      className="hidden md:flex w-64 bg-[#FDFCFB] border-r border-[#E8E6E1] flex-col h-screen sticky top-0"
      data-testid="app-sidebar"
    >
      <div className="px-6 pt-7 pb-5 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-[#2A4D3E] flex items-center justify-center">
          <Globe2 size={18} className="text-white" strokeWidth={1.8} />
        </div>
        <div>
          <div className="font-heading font-semibold text-[15px] text-[#2A332C] leading-tight">AIMS Healthcare</div>
          <div className="text-[10px] text-[#8E9992] uppercase tracking-[0.18em] mt-0.5">IPMP</div>
        </div>
      </div>

      <nav className="flex-1 px-2 mt-1 overflow-y-auto">
        {sections.map(({ section, items }) => (
          <div key={section} className="mb-4" data-testid={`nav-section-${section.toLowerCase()}`}>
            <div className="px-5 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A8B1AC]">
              {section}
            </div>
            {items.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-xl mx-2 mb-0.5 transition-all duration-200 ${
                    isActive
                      ? "bg-[#E6F0EB] text-[#2A4D3E]"
                      : "text-[#5C6660] hover:bg-[#F2F4F3] hover:text-[#2A332C]"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.7} />
                {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-[#E8E6E1] px-4 py-4">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#2A4D3E] flex items-center justify-center text-white font-medium text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-[#2A332C] truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[10px] text-[#8E9992] uppercase tracking-[0.12em]">{user?.role}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5C6660] hover:bg-[#F2F4F3] hover:text-[#C0392B] rounded-lg transition-colors"
        >
          <LogOut size={15} strokeWidth={1.6} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
